import React, { useState, useEffect, useMemo } from 'react';
import axios from '../utils/api';
import { HtmlTemplate, TemplateArea } from '../types/template';
import './TemplatePreview.css';

interface TemplatePreviewProps {
  template: HtmlTemplate;
  areas: TemplateArea[];
  onClose: () => void;
}

interface PreviewComponent {
  id: string;
  name: string;
  type: string;
  area_type: string;
  html_content: string;
  css_content: string;
}

interface AreaComponent {
  id: string | number;
  component_name: string;
  category: string;
  html_content?: string;
  css_content?: string;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ 
  template, 
  areas, 
  onClose 
}) => {
  const [previewComponents, setPreviewComponents] = useState<PreviewComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    generatePreview();
    // Нам важно не менять поведение (обновление предпросмотра при смене template/areas),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, areas]);

  const generatePreview = async () => {
    try {
      setLoading(true);
      
      // Получаем компоненты для каждой области
      const components: PreviewComponent[] = [];
      
      for (const area of areas) {
        if (area.is_active) {
          // Получаем компоненты для данной области
          const areaComponents = await fetchAreaComponents(area.area_type);
          
          if (areaComponents.length > 0) {
            // Выбираем случайный компонент для предпросмотра
            const randomComponent = areaComponents[Math.floor(Math.random() * areaComponents.length)];
            
            components.push({
              id: `${area.id}-${randomComponent.id}`,
              name: randomComponent.component_name,
              type: randomComponent.category,
              area_type: area.area_type,
              html_content: randomComponent.html_content || `<div class="preview-${area.area_type}">${area.name}</div>`,
              css_content: randomComponent.css_content || ''
            });
          } else {
            // Создаем заглушку для области
            components.push({
              id: `placeholder-${area.id}`,
              name: area.name,
              type: 'placeholder',
              area_type: area.area_type,
              html_content: `<div class="preview-${area.area_type} preview-placeholder">${area.name}</div>`,
              css_content: `
                .preview-${area.area_type} {
                  padding: 20px;
                  background: #f8f9fa;
                  border: 2px dashed #dee2e6;
                  border-radius: 8px;
                  text-align: center;
                  color: #6c757d;
                  font-weight: 500;
                }
                .preview-placeholder {
                  background: #fff3cd;
                  border-color: #ffc107;
                  color: #856404;
                }
              `
            });
          }
        }
      }
      
      setPreviewComponents(components);
    } catch (err) {
      console.error('Ошибка генерации предпросмотра:', err);
      setError('Не удалось сгенерировать предпросмотр');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreaComponents = async (areaType: string): Promise<AreaComponent[]> => {
    try {
      const response = await axios.get(`/api/admin/template-components?area_type=${areaType}`);
      return (response.data || []) as AreaComponent[];
    } catch (err) {
      console.error(`Ошибка загрузки компонентов для области ${areaType}:`, err);
      return [];
    }
  };

  const previewSrcDoc = useMemo(() => {
    if (!previewComponents.length) {
      return '';
    }

    const baseHref = typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/`
      : '/';

    const escapeFragment = (value: string) => String(value || '').replace(/`/g, '\\`');

    const hasArea = (areaType: string) => areas.some((area) => area.area_type === areaType && area.is_active);

    const baseStyles = `
      .template-preview {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.6;
        color: #333;
      }
      
      .template-preview * {
        box-sizing: border-box;
      }
      
      .template-preview-header {
        background: #fff;
        border-bottom: 1px solid #e9ecef;
        padding: 16px;
        text-align: center;
      }
      
      .template-preview-navigation {
        background: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
        padding: 12px 16px;
      }
      
      .template-preview-sidebar {
        background: #f8f9fa;
        border-right: 1px solid #e9ecef;
        padding: 16px;
      }
      
      .template-preview-dynamic {
        background: #fff;
        padding: 20px;
        min-height: 400px;
      }
      
      .template-preview-footer {
        background: #343a40;
        color: #fff;
        padding: 20px;
        text-align: center;
      }
      
      .preview-component {
        margin-bottom: 16px;
        padding: 12px;
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
      
      .preview-component-header {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        color: #6c757d;
        margin-bottom: 8px;
        letter-spacing: 0.5px;
      }
    `;

    // Добавляем стили для каждого компонента
    const componentStyles = previewComponents
      .map((comp) => comp.css_content)
      .filter(Boolean)
      .join('\n');

    const previewStyles = baseStyles + '\n' + componentStyles;

    const buildArea = (areaType: string, label: string, wrapperClass: string) => {
      const components = previewComponents.filter((comp) => comp.area_type === areaType);
      if (!components.length) {
        return '';
      }
      const items = components.map((comp) => `
        <div class="preview-component">
          <div class="preview-component-header">${label}</div>
          <div class="preview-component-body">${escapeFragment(comp.html_content)}</div>
        </div>
      `).join('');
      return `<div class="${wrapperClass}">${items}</div>`;
    };

    const header = hasArea('header') ? buildArea('header', 'Хедер', 'template-preview-header') : '';
    const navigation = hasArea('navigation') ? buildArea('navigation', 'Навигация', 'template-preview-navigation') : '';
    const sidebar = hasArea('sidebar') ? buildArea('sidebar', 'Сайдбар', 'template-preview-sidebar') : '';
    const dynamic = hasArea('dynamic') ? buildArea('dynamic', 'Динамический контент', 'template-preview-dynamic') : '';
    const footer = hasArea('footer') ? buildArea('footer', 'Футер', 'template-preview-footer') : '';
    const content = sidebar || dynamic ? `<div class="template-preview-content">${sidebar}${dynamic}</div>` : '';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <base href="${baseHref}" />
  <style>${previewStyles}</style>
</head>
<body>
  <div class="template-preview template-preview--${previewMode}">
    ${header}
    ${navigation}
    ${content}
    ${footer}
  </div>
</body>
</html>`;
  }, [previewComponents, previewMode, areas]);

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="template-preview__loading">
          <div className="spinner"></div>
          <p>Генерация предпросмотра...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="template-preview__error">
          <p>{error}</p>
          <button onClick={generatePreview} className="btn btn-primary">
            Попробовать снова
          </button>
        </div>
      );
    }

    return (
      <div className={`template-preview-frame template-preview-frame--${previewMode}`}>
        <iframe
          className="template-preview-frame__iframe"
          sandbox="allow-scripts"
          srcDoc={previewSrcDoc}
          title="Template Preview"
        />
      </div>
    );
  };

  return (
    <div className="template-preview-modal">
      <div className="template-preview-modal__overlay" onClick={onClose}></div>
      
      <div className="template-preview-modal__content">
        <div className="template-preview-modal__header">
          <h3>Предпросмотр шаблона: {template.name}</h3>
          
          <div className="template-preview-modal__controls">
            <div className="template-preview-modal__mode-switcher">
              <button
                className={`btn btn-sm ${previewMode === 'desktop' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPreviewMode('desktop')}
              >
                🖥️ Desktop
              </button>
              <button
                className={`btn btn-sm ${previewMode === 'tablet' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPreviewMode('tablet')}
              >
                📱 Tablet
              </button>
              <button
                className={`btn btn-sm ${previewMode === 'mobile' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPreviewMode('mobile')}
              >
                📱 Mobile
              </button>
            </div>
            
            <button
              className="btn btn-secondary"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="template-preview-modal__body">
          {renderPreviewContent()}
        </div>

        <div className="template-preview-modal__footer">
          <div className="template-preview-modal__info">
            <p>
              <strong>Области:</strong> {areas.filter(a => a.is_active).length} активных
            </p>
            <p>
              <strong>Компоненты:</strong> {previewComponents.length} загружено
            </p>
          </div>
          
          <div className="template-preview-modal__actions">
            <button
              className="btn btn-primary"
              onClick={generatePreview}
              disabled={loading}
            >
              🔄 Обновить
            </button>
            <button
              className="btn btn-secondary"
              onClick={onClose}
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
